<template>
    <auth-layout
        :image="1"
        :byword="$t('pages.auth.signup.steps.register.byword')"
        :progress="progress"
        :isProgressBarHidden="true"
        :isPageLoading="isPagePreparing"
    >
        <common-content-wrapper width="small">
            <common-page-header :heading="getHeading">
                <template
                    v-if="dataForPrefill.practiceName && invitee?.inviteType !== JoinInviteTypeEnum.REFERRAL_CREATION"
                    #content
                >
                    {{ $t('pages.auth.signup.steps.register.description.invite') }}
                    <br />
                    <b>{{ dataForPrefill.practiceName }}</b>
                </template>
                <template v-else-if="invitee?.inviteType !== JoinInviteTypeEnum.REFERRAL_CREATION" #content>
                    {{ $t('pages.auth.signup.steps.register.offer') }}
                </template>
            </common-page-header>
            <common-modal v-if="billingPricingModalOpen" width="pricing" @close="toggleBillingPricingModal">
                <pricing-cards :showButtons="false" :useFallback="true"></pricing-cards>
            </common-modal>
        </common-content-wrapper>

        <common-content-wrapper width="small">
            <auth-register-with-npi-form
                v-if="isSignUpWithNpiEnabled"
                @go-wtihout-npi="goWithoutNpi"
                @provider-data-matched="handleProviderDataMatched"
            ></auth-register-with-npi-form>
            <register-form
                v-else
                :organizationRoles="filteredOrganizationRoles"
                :specialities="specialities"
                :physicianGroups="physicianGroups"
                :dataForPrefill="dataForPrefill"
                :isComingFromInvite="isComingFromInvite"
                :fieldsToHide="fieldsToHide"
                :invitee="invitee"
                @sent="goToNextStep"
                @form-submitted="isFormSubmitted($event)"
            ></register-form>
        </common-content-wrapper>
        <common-content-wrapper width="small">
            <div class="trial-info">
                <button type="button" class="trial-info__toggle" :aria-expanded="isTrialInfoOpen" @click="isTrialInfoOpen = !isTrialInfoOpen">
                    <span class="trial-info__icon" aria-hidden="true">i</span>
                    {{ $t('pages.auth.signup.steps.register.trial_info_toggle') }}
                </button>
                <p v-if="isTrialInfoOpen" class="trial-info__text">
                    {{ $t('pages.auth.signup.steps.register.trial_info') }}
                    <a @click="toggleBillingPricingModal">{{ $t('pages.practice.billing.see_plans') }}</a>
                </p>
            </div>
            <div v-if="!isSignUpWithNpiEnabled && !$route.query.npi" class="find-npi" @click="isSignUpWithNpiEnabled = true">
                <common-icon name="menu-arrow" size="medium" class="find-npi__icon"></common-icon>
                <span>{{ $t('pages.auth.signup.demo.go_back') }}</span>
            </div>
        </common-content-wrapper>
    </auth-layout>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import UserRolesAPI from 'api/UserRolesAPI'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import RegisterForm from 'components/forms/AuthSignupRegisterForm'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import PhysicianGroupsAPI from 'api/PhysicianGroupsAPI'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'
    import JoinInviteTypeEnum from '@/enums/JoinInviteTypeEnum'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import AuthRegisterWithNpiForm from 'components/forms/auth/AuthRegisterWithNpiForm'

    // Practice types a sign-up link may preset with ?pt=
    const SIGNUP_PRACTICE_TYPES = [PracticeTypeEnum.PHYSICIAN, PracticeTypeEnum.ANCILLARY, PracticeTypeEnum.OTHER]

    export default {
        metaInfo() {
            if (process.env.VUE_APP_ENV === 'production') {
                return {
                    title: this.$t('pages.auth.signup.title'),
                    meta: [
                        { name: 'description', content: 'pages.auth.signup.page_description' },
                        { name: 'keywords', content: this.$t('pages.auth.signup.page_keywords') },
                        { name: 'author', content: this.$t('pages.auth.signup.page_author') },
                        { name: 'robots', content: this.$t('pages.auth.signup.page_robots') },
                    ],
                }
            }
        },

        mixins: [SignupPageMixin, PageWithDataMixin],

        components: {
            AuthLayout,
            RegisterForm,
            PricingCards,
            AuthRegisterWithNpiForm,
        },

        data() {
            return {
                organizationRoles: [],
                specialities: [],
                physicianGroups: [],
                dataForPrefill: {},
                isComingFromInvite: false,
                isDisabled: false,
                billingPricingModalOpen: false,
                isTrialInfoOpen: false,
                isPagePreparing: false,
                fieldsToHide: [],
                invitee: {},
                JoinInviteTypeEnum,
                isSignUpWithNpiEnabled: true,
            }
        },

        computed: {
            loginLinkClass() {
                return {
                    'login-link__disabled': this.isDisabled,
                }
            },

            getHeading() {
                return this.invitee?.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION
                    ? this.$t('pages.auth.signup.steps.register.heading_outsider')
                    : this.$t('pages.auth.signup.steps.register.heading_welcome')
            },

            filteredOrganizationRoles() {
                if (this.invitee?.inviteType === JoinInviteTypeEnum.NETWORK) {
                    return this.organizationRoles.filter((role) =>
                        [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(role.data.id),
                    )
                }
                return this.organizationRoles
            },
        },

        methods: {
            isFormSubmitted(event) {
                this.isDisabled = event.loginLinkDisabled ? true : false
            },

            toggleBillingPricingModal() {
                this.billingPricingModalOpen = !this.billingPricingModalOpen
            },

            getPageData() {
                // Fetch specialities first to avoid race condition
                SpecialitiesAPI.getListMultiselect().then((specialities) => {
                    this.specialities = specialities
                    Promise.all([UserRolesAPI.getList(), PhysicianGroupsAPI.getList()])
                        .then(([organizationRoles, physicianGroups]) => {
                            this.organizationRoles = organizationRoles
                            this.physicianGroups = physicianGroups
                        })
                        .finally(() => {
                            this.isPagePreparing = false
                        })
                })
            },

            // The practice type the sign-up link asked for (?pt=ancillary on the ancillary page). The registry match
            // must not wipe it: a registry entry only tells us whether the provider is a physician.
            requestedPracticeType() {
                const requested = this.$route.query.pt
                return SIGNUP_PRACTICE_TYPES.includes(requested) ? requested : null
            },

            buildNpiPrefill(res) {
                // A physician's NPI means a provider practice, even on an ancillary link (Amos, 2026-11-03). The link's
                // type applies to everyone else; the person can still change the choice on the form. isPhysician alone
                // isn't enough: an organization's NPI, such as a radiology center's, can carry it but has no person's name.
                const isPerson = res.isPhysician && !!res.firstName
                const practiceType = isPerson ? PracticeTypeEnum.PHYSICIAN : this.requestedPracticeType() || undefined
                const prefill = {
                    firstName: res.firstName,
                    lastName: res.lastName,
                    email: res.email ?? res.businessEmail,
                    phone: res.phone,
                    designation: res.designation,
                    specialities: res.specialities,
                }
                // Only set what is known, so an unknown type or role does not clear a value already chosen.
                if (practiceType) prefill.practiceType = practiceType
                if (practiceType === PracticeTypeEnum.PHYSICIAN && isPerson) {
                    prefill.userRoleId = UserRolesEnum.PHYSICIAN.ID
                }
                return prefill
            },

            fetchUsersDataByNpi() {
                ProviderDatabaseAPI.get(this.$route.query.npi).then((res) => {
                    if (!res) return

                    this.dataForPrefill = this.buildNpiPrefill(res)
                })
            },

            handleProviderDataMatched(res) {
                this.isSignUpWithNpiEnabled = false
                this.$nextTick(() => {
                    this.dataForPrefill = this.buildNpiPrefill(res)
                })
            },

            goWithoutNpi() {
                this.isSignUpWithNpiEnabled = false
                // Keep the link's query (?pt=ancillary and any prefill), without an NPI.
                const query = { ...this.$route.query }
                delete query.npi
                this.$router.push({ name: 'auth.signup.register', query })
            },

            generatePhysicianGroupId(invitee) {
                return invitee.inviteType === JoinInviteTypeEnum.PRACTICE
                    ? invitee.physicianGroupId
                    : invitee.practice?.groupId ?? null
            },
        },

        mounted() {
            this.physicianGroups = [{ label: 'None', value: -1, data: { displayName: 'None', id: -1 } }, ...this.physicianGroups]
            const joinInviteToken = Object.keys(this.$route.query)
                .filter((key) => /^.*jit$/.test(key))
                .map((key) => this.$route.query[key])[0]
            if (joinInviteToken && /^[0-9A-Fa-f]+$/.test(joinInviteToken)) {
                this.isSignUpWithNpiEnabled = false
                UsersAPI.getJoinInviteByToken(joinInviteToken, [
                    'id',
                    'toEmail',
                    'practiceType',
                    'practiceName',
                    'practice(id,name,practiceType,groupId)',
                    'userRoleId',
                    'inviteType',
                    'fromUserRoleId',
                    'firstName',
                    'lastName',
                    'designation',
                    'specialities',
                    'physicianGroupId',
                    'canActivateAnotherUser',
                ])
                    .then((res) => {
                        this.invitee = res
                        const dataForPrefill = {
                            joinInviteToken,
                            email: res.toEmail,
                            firstName: res.firstName,
                            lastName: res.lastName,
                            practiceName: res.practiceName,
                            userRoleId: res.userRoleId,
                            physicianGroupId: this.generatePhysicianGroupId(res),
                            designation: res.designation,
                            specialities: res.specialities,
                        }
                        if (res.userRoleId === UserRolesEnum.NURSE_PRACTITIONER.ID) {
                            dataForPrefill.canActivateAnotherUser = !!res.canActivateAnotherUser
                        }
                        if (res.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION) {
                            this.fieldsToHide = ['phone']
                        }
                        if (res.practice) {
                            dataForPrefill.practiceName =
                                res.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION ? undefined : res.practice.name
                            dataForPrefill.practiceId = res.practice.id
                        }
                        if (res.practiceType) {
                            dataForPrefill.practiceType = res.practiceType
                        }
                        this.dataForPrefill = dataForPrefill
                        this.isComingFromInvite = true
                    })
                    .catch(() => {})
            }

            if (this.$route.query.npi) {
                this.isSignUpWithNpiEnabled = false
                this.fetchUsersDataByNpi()
            }
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = 'mobile'
        },

        beforeDestroy() {
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = ''
        },
        created() {
            this.getPageData()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    .login {
        &-link {
            &__disabled {
                pointer-events: none;
                opacity: 0.2;
            }
        }
    }

    .trial-info {
        text-align: center;

        &__toggle {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 0;
            border: 0;
            background: none;
            cursor: pointer;
            font: inherit;
            font-size: 14px;
            color: $color--accent-1;
        }

        &__icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            border: 1px solid currentColor;
            border-radius: 50%;
            font-size: 12px;
            font-weight: bold;
        }

        &__text {
            margin: 10px 0 0;
            font-size: 14px;
            text-align: left;
        }
    }

    .find-npi {
        text-align: center;
        cursor: pointer;
        color: $color--accent-1;
        margin-top: 40px;

        &__icon {
            rotate: 180deg;
        }
    }
</style>
